import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NavigateFunction } from 'react-router-dom';

import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import { BancaCliente, BancaClientePagingRes } from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import { getUrlParamsFetch, handleAxiosError } from '@/shared';

const { get, post, put } = erpApi();

///* tanSctack query
export const useFetchBancaClientes = ({
  page = 1,
  page_size = 10,
  nombre = '',
}: GetBancaClientesParams) => {
  return useQuery({
    queryKey: ['bancaClientes', page, nombre, page_size],
    queryFn: async () => {
      return getBancaClientes({ page, nombre, page_size });
    },
  });
};

export const useGetBancaCliente = (id: string) => {
  return useQuery({
    queryKey: ['bancaClientes', id],
    queryFn: async () => {
      return getBancaCliente(id);
    },
  });
};

export const useCreateBancaCliente = (navigate: NavigateFunction, returnUrl: string,
  enableNavigation = true) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createBancaCliente,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bancaClientes'] });
      enableNavigation && navigate(returnUrl, { replace: true });
    },
    onError: error => {
      handleAxiosError(error);
    },
  });
};

export const useUpdateBancaCliente = (navigate: NavigateFunction, returnUrl: string,
  noState?: boolean,
  enableNavigation?: boolean
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateBancaCliente,
    onSuccess: () => {
      noState && queryClient.invalidateQueries({ queryKey: ['bancaClientes'] });
      enableNavigation && navigate(returnUrl, { replace: true });
    },
    onError: error => {
      handleAxiosError(error);
    },
  });
};

///* axios
export type GetBancaClientesParams = {
  page?: number;
  page_size?: number;
  nombre?: string;
};

export type CreateBancaClienteParams = Omit<BancaCliente, 'id_banca_cliente'>;
export type UpdateBancaClienteParams = {
  id: string;
  data: CreateBancaClienteParams;
};

export const getBancaClientes = async (params?: GetBancaClientesParams) => {
  const queryParams = getUrlParamsFetch({ ...params });

  return get<BancaClientePagingRes>(
    `${urlBackend}api/banca-cliente/?${queryParams}`,
    true
  );
};

export const getBancaCliente = async (id: string) => {
  try{
    return await get<BancaCliente>(`${urlBackend}api/banca-cliente/${id}/`, true);
  }
  catch(error){
    return handleAxiosError(error);
  }
};

export const createBancaCliente = async (data: CreateBancaClienteParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await post<BancaCliente>(`${urlBackend}api/v1/banca-cliente/`, data, true);

  setAlert(true, res.message, res.status);
  return res;
};

export const updateBancaCliente = async ({ id, data }: UpdateBancaClienteParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put<BancaCliente>(`${urlBackend}api/v1/banca-cliente/${id}/`, data, true);

  setAlert(true, res.message, res.status);
  return res;
};
